import {handleActions} from 'redux-actions';

import {
  GET_ABACUS_CONVERSATION,
  RESET_ABACUS_STATE,
  SET_ABACUS_ERROR,
  SET_ABACUS_LOADING,
} from '/redux/action/abacus.action';
import {withPayload} from '/util/reducer.util';

import {AbacusMessageRecord, AbacusState} from '/types/abacus.types';

const createInitialState = (): AbacusState => ({
  isLoading: false,
  error: null,
  conversationId: null,
  externalSessionId: null,
  lastResponse: null,
  messages: [],
  lastUpdatedAt: null,
});

const updateLoading = (
  state: AbacusState,
  payload: {isLoading?: boolean},
): AbacusState => ({
  ...state,
  isLoading: Boolean(payload?.isLoading),
});

const updateError = (
  state: AbacusState,
  payload: {error?: string | null},
): AbacusState => ({
  ...state,
  error: payload?.error ?? null,
});

const extractConversationIdFromSegments = (response: any): string | null => {
  const segments = response?.result?.segments ?? response?.segments;
  if (!Array.isArray(segments)) {
    return null;
  }
  for (const seg of segments) {
    if (
      seg &&
      typeof seg === 'object' &&
      seg.id === 'conversation_id' &&
      typeof seg.segment === 'string' &&
      seg.segment.trim().length > 0
    ) {
      return seg.segment.trim();
    }
  }
  return null;
};

const extractDeploymentConversationIdFromSegments = (
  response: any,
): string | null => {
  const segments = response?.result?.segments ?? response?.segments;
  if (!Array.isArray(segments)) {
    return null;
  }

  for (const seg of segments) {
    if (!seg || typeof seg !== 'object') {
      continue;
    }

    if (
      typeof seg.deploymentConversationId === 'string' &&
      seg.deploymentConversationId.trim().length > 0
    ) {
      return seg.deploymentConversationId.trim();
    }

    if (
      seg.segment &&
      typeof seg.segment === 'object' &&
      typeof seg.segment.deploymentConversationId === 'string' &&
      seg.segment.deploymentConversationId.trim().length > 0
    ) {
      return seg.segment.deploymentConversationId.trim();
    }
  }

  return null;
};

const appendConversationEntry = (
  state: AbacusState,
  payload: any,
): AbacusState => {
  const {
    response = null,
    message = '',
    options = null,
    requestId,
    timestamp,
  } = payload || {};

  const nextTimestamp =
    typeof timestamp === 'string' && timestamp.length > 0
      ? timestamp
      : new Date().toISOString();

  const resolvedConversationId =
    response?.deploymentConversationId ??
    response?.deploymentConversationID ??
    response?.deployment_conversation_id ??
    extractDeploymentConversationIdFromSegments(response) ??
    response?.conversationId ??
    response?.conversationID ??
    extractConversationIdFromSegments(response) ??
    state.conversationId ??
    null;

  const resolvedExternalSessionId =
    response?.externalSessionId ?? state.externalSessionId ?? null;

  const entry: AbacusMessageRecord = {
    id:
      typeof requestId === 'string' && requestId.length > 0
        ? requestId
        : `abacus-${Date.now()}`,
    message,
    timestamp: nextTimestamp,
    options: options ?? null,
    response,
  };

  return {
    ...state,
    error: null,
    conversationId: resolvedConversationId,
    externalSessionId: resolvedExternalSessionId,
    lastResponse: response,
    lastUpdatedAt: nextTimestamp,
    messages: [...state.messages, entry],
  };
};

const initialState = createInitialState();

export const abacus = handleActions<AbacusState, any>(
  {
    [SET_ABACUS_LOADING]: withPayload(updateLoading),
    [SET_ABACUS_ERROR]: withPayload(updateError),
    [RESET_ABACUS_STATE]: () => createInitialState(),
    [GET_ABACUS_CONVERSATION]: withPayload(appendConversationEntry),
  },
  initialState,
);

export default abacus;
